import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { closeDialog } from '@shared/store/dialog.duck';
import { BasicUserInfo } from '@shared/models/user.models';
import { findUserRequest } from '@features/settings/services/UserService';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import TextField from '@shared/ui/atoms/TextField';
import Dialog from '@shared/ui/molecules/Dialog';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { naturalSort } from '@shared/utils/ArrayUtils';
import { useTranslation } from "react-i18next";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export enum UserSelectType {
  multiSelect, singleSelect
}

export interface UserSearchDialogPayload {
  onClose: any;
  title: string;
  description: string;
  type: UserSelectType;
  selectedUsers?: BasicUserInfo[];
  onUserSelect: any;
}

export default function UserSearchDialog(p: { payload?: UserSearchDialogPayload }) {
  const props = p.payload ?? ({} as UserSearchDialogPayload);
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<BasicUserInfo[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<BasicUserInfo[]>(props.selectedUsers ?? []);
  const {t} = useTranslation();

  useEffect(() => {
    const id = setTimeout(() => {
      findUserRequest(searchQuery)
        .then((result: BasicUserInfo[]) => naturalSort(result, 'fullName'))
        .then((result: BasicUserInfo[]) => setUsers(result));
    }, 250);

    return () => {
      clearTimeout(id);
    }
  }, [searchQuery]);

  const close = (result?: any) => {
    if (props.onClose) props.onClose(result);
    dispatch(closeDialog());
  }
  const onChange = (val: string) => setSearchQuery(val);

  const onUserSelect = (selectedUser: BasicUserInfo) => {
    if (props.onUserSelect) {
      props.onUserSelect(selectedUser);
    }
    if (props.type === UserSelectType.singleSelect) {
      close(selectedUser);
    } else {
      if (selectedUsers.includes(selectedUser)) {
        setSelectedUsers(selectedUsers.filter(su => su.userId !== selectedUser.userId));
      } else {
        setSelectedUsers([...selectedUsers, selectedUser]);
      }
    }
  }

  const done = () => {
    close(props.type === UserSelectType.multiSelect ? selectedUsers : undefined);
  }

  return (
    <Dialog isOpen={true} onClose={() => close()}>
      <DialogHeader title={props.title} />
      {props.type === UserSelectType.multiSelect && <BodyRegular style={{marginBottom: 6}}>{t(props.description, { userCount: selectedUsers.length })}</BodyRegular>}
      <TextField value={searchQuery} onChange={onChange} />
      <SearchResults className={"searchDialog__results"}>
        {users.map((user: BasicUserInfo, key: number) => (
          <UserInfoRow key={key} className={'searchDialog__result'}>
            <UserAvatarAndName firstName={user.firstName} lastName={user.lastName} />
            <Checkbox value={!!selectedUsers.find(su => su.userId === user.userId)} onChange={() => onUserSelect(user)} />
          </UserInfoRow>
        ))}
      </SearchResults>
      <PrimaryButton click={done} text="button.done" size={ButtonSize.Large} fullWidth={true} dataTest={"user-search-dialog-done"} />
    </Dialog>
  )
}

const SearchResults = styled.div`
  margin-bottom: 32px;
  height: 250px;
  overflow-y: auto;
`

const UserInfoRow = styled.div<any>`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  .userAvatarAndName {
    cursor: pointer;
  }
`
